import { useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, SearchBar, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", unite: "kg", quantite: "", seuil_alerte: "", cout_unitaire: "", fournisseur: "" };

export default function Stocks() {
  const { isAdmin, can } = useAuth();
  const { data, loading, refetch } = useData("/stock");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ nom: s.nom, unite: s.unite, quantite: s.quantite, seuil_alerte: s.seuil_alerte, cout_unitaire: s.cout_unitaire, fournisseur: s.fournisseur || "" });
    setEditId(s.id); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, quantite: parseFloat(form.quantite), seuil_alerte: parseFloat(form.seuil_alerte), cout_unitaire: parseFloat(form.cout_unitaire) };
    try {
      if (editId) await api.put(`/stock/${editId}`, payload);
      else await api.post(`/stock`, payload);
      toast.success(editId ? "Ingrédient modifié" : "Ingrédient ajouté");
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer ${s.nom} ?`)) return;
    await api.delete(`/stock/${s.id}`);
    toast.success("Supprimé"); refetch();
  };

  const cols = [
    { header: "Ingrédient", value: (r) => r.nom },
    { header: "Quantité", value: (r) => r.quantite },
    { header: "Unité", value: (r) => r.unite },
    { header: "Seuil alerte", value: (r) => r.seuil_alerte },
    { header: "Coût unitaire (DH)", value: (r) => r.cout_unitaire },
    { header: "Valeur (DH)", value: (r) => (r.quantite * r.cout_unitaire).toFixed(2) },
  ];

  const totalValue = data.reduce((a, s) => a + s.quantite * s.cout_unitaire, 0);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? data.filter((s) => [s.nom, s.unite, s.fournisseur].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : data;

  return (
    <div>
      <PageHeader title="Stock du Laboratoire" subtitle={`Source unique des matières premières. Valeur totale du stock : ${MAD(totalValue)}`}>
        {can("stocks", "export") && <ExportButtons onExcel={() => exportExcel("stocks_tiramessi", cols, data)} onPDF={() => exportPDF("Stocks TIRAMESSI", cols, data)} />}
        {can("stocks", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-stock-btn"><Plus size={16} /> Nouvel ingrédient</button>}
      </PageHeader>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucun ingrédient." /> : (
        <>
          <SearchBar value={query} onChange={setQuery} placeholder="Rechercher un ingrédient, une unité, un fournisseur..." testid="stock-search" count={filtered.length} total={data.length} />
          {filtered.length === 0 ? <Empty text="Aucun résultat pour cette recherche." /> : (
          <div className="tm-card overflow-x-auto" data-testid="stocks-table">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Ingrédient</th><th className="text-right">Quantité</th><th className="text-right">Seuil</th>
                <th className="text-right">Coût unit.</th><th className="text-right">Valeur</th><th>État</th>{(can("stocks", "edit") || can("stocks", "delete")) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const low = s.quantite <= s.seuil_alerte;
                return (
                  <tr key={s.id} data-testid={`stock-row-${s.id}`}>
                    <td className="font-semibold">{s.nom}</td>
                    <td className="text-right tabular">{s.quantite} {s.unite}</td>
                    <td className="text-right tabular text-[var(--tm-muted)]">{s.seuil_alerte} {s.unite}</td>
                    <td className="text-right tabular text-[var(--tm-muted)]">{MAD(s.cout_unitaire)}</td>
                    <td className="text-right tabular">{MAD(s.quantite * s.cout_unitaire)}</td>
                    <td>
                      {low ? (
                        <span className="tm-badge" style={{ background: "rgba(255,152,0,.15)", color: "#FF9800" }}><AlertTriangle size={12} /> Faible</span>
                      ) : (
                        <span className="tm-badge" style={{ background: "rgba(76,175,80,.15)", color: "#4CAF50" }}>OK</span>
                      )}
                    </td>
                    {(can("stocks", "edit") || can("stocks", "delete")) && (
                      <td className="text-right whitespace-nowrap">
                        {can("stocks", "edit") && <button onClick={() => openEdit(s)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`edit-stock-${s.id}`}><Pencil size={16} /></button>}
                        {can("stocks", "delete") && <button onClick={() => remove(s)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={16} /></button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier l'ingrédient" : "Nouvel ingrédient"} testid="stock-modal">
        <form onSubmit={save}>
          <Field label="Nom"><input className="tm-input" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="stock-nom" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantité"><input type="number" step="0.001" className="tm-input" required value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="stock-quantite" /></Field>
            <Field label="Unité">
              <select className="tm-input" value={form.unite} onChange={(e) => setForm({ ...form, unite: e.target.value })}>
                <option>kg</option><option>g</option><option>L</option><option>ml</option><option>unité</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Seuil alerte"><input type="number" step="0.001" className="tm-input" required value={form.seuil_alerte} onChange={(e) => setForm({ ...form, seuil_alerte: e.target.value })} data-testid="stock-seuil" /></Field>
            <Field label="Coût unitaire (DH)"><input type="number" step="0.01" className="tm-input" required value={form.cout_unitaire} onChange={(e) => setForm({ ...form, cout_unitaire: e.target.value })} /></Field>
          </div>
          <Field label="Fournisseur"><input className="tm-input" value={form.fournisseur} onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} /></Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="stock-save">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
}
